import { useMutation } from '@apollo/client'
import { ResultOf } from '@graphql-typed-document-node/core'

import { extractByTypename } from '@local/utils'

import { openToast } from '@web/components/Toast/GlobalToastPortal'
import { gql } from '@web/federation/gql'
import { useMapWidget } from '@web/hooks/useMapWidget'
import { federationApolloClient } from '@web/libs/apollo/federationClient'
import { neloLogger } from '@web/libs/utils'
import { Nullish } from '@web/types'

const BookmarkButtonFragment = gql(`
  fragment useBookmark_PlaceSummary on PlaceSummary {
    id
    isBookmarked
    notExist 
  }
`)

const DeleteBookmarkMutation = gql(`
mutation useBookmark_DeleteBookmarkMutation($input: DeleteBookmarkByPlaceIdInput!) {
  deleteBookmarkByPlaceId(input: $input) {
    ... on DeleteBookmark_Succeed {
      bookmarkId
    }
  }
}
`)

interface Props {
  place?: Nullish<ResultOf<typeof BookmarkButtonFragment>>
}

export const useBookmark = () => {
  const { openBookmark } = useMapWidget()
  const [deleteBookmarkMutation] = useMutation(DeleteBookmarkMutation)

  const handleBookmark = ({ place }: Props) => {
    if (!place) {
      return {
        isBookmarked: false,
        openBookmarkModal: () => {},
        isAvailableBookmark: false,
      }
    }

    const isAvailableBookmark = (() => {
      if (place.isBookmarked) return true

      if (!place.id) return false

      return !place.notExist
    })()

    const openBookmarkModal = async () => {
      // 폐업인데 북마크가 있을 경우, 해제합니다.
      if (place.notExist && place.isBookmarked) {
        await deleteBookmarkMutation({
          variables: {
            input: {
              placeId: place.id as string,
            },
          },
          onCompleted(result) {
            const { DeleteBookmark_Succeed } = extractByTypename(result.deleteBookmarkByPlaceId)

            if (DeleteBookmark_Succeed) {
              federationApolloClient?.cache.writeFragment({
                id: federationApolloClient.cache.identify(place),
                fragment: BookmarkButtonFragment,
                data: {
                  ...place,
                  isBookmarked: false,
                },
              })

              return
            }
            neloLogger('error', '북마크 제거를 실패했습니다.', { place })
            openToast({
              title: '일부 정보를 불러오는데 실패했습니다.',
              text: '잠시 후 다시 시도해주세요',
              iconType: 'error',
            })
          },
          onError: () => {
            openToast({
              title: '일부 정보를 불러오는데 실패했습니다.',
              text: '잠시 후 다시 시도해주세요',
              iconType: 'error',
            })
          },
        })
        return
      }

      if (!isAvailableBookmark) return

      if (!place.id) return

      openBookmark({
        placeId: place.id,
        handleBookmarkChange: (updatedBookmarked: boolean) => {
          federationApolloClient?.cache.writeFragment({
            id: federationApolloClient.cache.identify(place),
            fragment: BookmarkButtonFragment,
            data: {
              ...place,
              isBookmarked: updatedBookmarked,
            },
          })
        },
      })
    }

    return {
      isBookmarked: place.isBookmarked ?? false,
      openBookmarkModal,
      isAvailableBookmark,
    }
  }
  return {
    handleBookmark,
  }
}
